import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Search, Headphones, ChevronLeft, ChevronRight, Loader2, BookOpen, User, Globe, 
  ChevronDown, Library, Zap, X, ChevronUp, Tag, SlidersHorizontal, MoreVertical, Heart, RefreshCw, Globe2, Sparkles,
  Star, Trash2
} from 'lucide-react';
import { librivoxApi, LibriVoxBook } from './librivoxApi';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AppBackButton } from '../components/AppBackButton';
import { useSyncedAudiobook } from '../contexts/SyncedAudiobookContext';
import { useAuth } from '../contexts/AuthContext';
import T from '../components/T';
import i18nInstance from '../i18n';
import { decryptWindowPayload } from '../utils/audiobookCrypto';
import { loadSavedSyncOffset } from '../services/syncAudiobookStorage';
import AudiobookDetailsModal, { AudiobookCardItem } from './AudiobookDetailsModal';
import { RatingBadge, RatingModal } from '../components/RatingStars';

// Capa dos audiobooks v3 com a mesma cadeia de reservas da lista
const SyncedCover: React.FC<{ item: { id: string; title: string; cover_url?: string } }> = ({ item }) => (
  <img
    src={item.cover_url || `https://www.gutenberg.org/cache/epub/${item.id}/pg${item.id}.cover.medium.jpg`}
    className="w-full h-full object-cover"
    alt={item.title}
    onError={(e) => {
      const img = e.currentTarget;
      if (img.src.includes('gutenberg.org')) {
        img.src = `https://covers.openlibrary.org/b/title/${encodeURIComponent(item.title)}-L.jpg`;
      } else if (img.src.includes('openlibrary.org')) {
        img.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.title)}&background=f59e0b&color=000&size=512&bold=true`;
      } else {
        img.onerror = null;
      }
    }}
  />
);

const LIBRIVOX_LANGUAGES = [
  { code: '', name: 'Todos os idiomas', flag: '🌐' },
  { code: 'English', name: 'Inglês', flag: '🇺🇸' },
  { code: 'German', name: 'Alemão', flag: '🇩🇪' },
  { code: 'French', name: 'Francês', flag: '🇫🇷' },
  { code: 'Spanish', name: 'Espanhol', flag: '🇪🇸' },
  { code: 'Italian', name: 'Italiano', flag: '🇮🇹' },
  { code: 'Chinese', name: 'Chinês', flag: '🇨🇳' },
  { code: 'Portuguese', name: 'Português', flag: '🇧🇷' },
  { code: 'Dutch', name: 'Holandês', flag: '🇳🇱' },
  { code: 'Latin', name: 'Latim', flag: '🏛️' },
  { code: 'Russian', name: 'Russo', flag: '🇷🇺' },
  { code: 'Finnish', name: 'Finlandês', flag: '🇫🇮' }
];

// Fase de teste do player v3: /audiobooks mostra só os livros v3 (descobertos no bucket libritui-protegido).
// Nada é apagado do R2. Para voltar a mostrar tudo: SHOW_ONLY_TEST_BOOKS = false aqui e
// ONLY_V3_IN_CATALOG = false em functions/_lib/v3Audiobooks.ts.
const SHOW_ONLY_TEST_BOOKS = true;

const PAGES_PER_WINDOW = 5;
const ITEMS_PER_PAGE = 6;

const formatDuration = (totalSecsOrTime?: number | string): string => {
  if (!totalSecsOrTime) return '';
  if (typeof totalSecsOrTime === 'number') {
    if (totalSecsOrTime <= 0) return '';
    const hours = Math.floor(totalSecsOrTime / 3600);
    const mins = Math.floor((totalSecsOrTime % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  }
  if (typeof totalSecsOrTime === 'string') {
    const parts = totalSecsOrTime.split(':').map(Number);
    if (parts.length === 3) {
      const [h, m] = parts;
      if (h > 0) return `${h}h ${m}m`;
      return `${m}m`;
    }
  }
  return '';
};

// Filter Modal component styled identical to /libritui-library (Image 2 style)
// Filter Modal component styled identical to /libritui-library (Image 3 style)
const FilterModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  selectedLang: string;
  onSelectLang: (code: string) => void;
  selectedLevel: string;
  onSelectLevel: (lvl: string) => void;
  selectedTag: string;
  onSelectTag: (tag: string) => void;
  sortBy: 'favoritos' | 'mais_acessados' | 'relevancia';
  onSelectSort: (sort: 'favoritos' | 'mais_acessados' | 'relevancia') => void;
}> = ({ isOpen, onClose, selectedLang, onSelectLang, selectedLevel, onSelectLevel, selectedTag, onSelectTag, sortBy, onSelectSort }) => {
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState<string | null>(null);

  if (!isOpen) return null;

  const toggleSection = (section: string) => {
    setOpenSection(prev => prev === section ? null : section);
  };

  const languages = [
    { code: '', name: 'Todos os Idiomas' },
    { code: 'English', name: 'Inglês' },
    { code: 'Portuguese', name: 'Português' },
    { code: 'Spanish', name: 'Espanhol' },
    { code: 'French', name: 'Francês' },
    { code: 'German', name: 'Alemão' },
    { code: 'Italian', name: 'Italiano' },
    { code: 'Dutch', name: 'Holandês' }
  ];

  const levels = [
    { id: '', label: 'Todos os Níveis' },
    { id: 'a1_a2', label: 'Iniciante (A1-A2)' },
    { id: 'b1_b2', label: 'Intermediário (B1-B2)' },
    { id: 'c1_c2', label: 'Avançado (C1-C2)' }
  ];

  const tagOptions = [
    { id: '', label: 'Todas as Tags' },
    { id: 'classicos', label: 'Clássicos' },
    { id: 'romance', label: 'Romance' },
    { id: 'ficcao', label: 'Ficção' },
    { id: 'historia', label: 'História' }
  ];

  const sortOptions = [
    { id: 'relevancia', label: 'Mais Relevantes' },
    { id: 'mais_acessados', label: 'Mais Acessados' },
    { id: 'favoritos', label: 'Favoritos' }
  ];

  const getSelectedLabel = (section: string) => {
    if (section === 'lang') return selectedLang ? languages.find(l => l.code === selectedLang || l.code.toLowerCase().slice(0, 2) === selectedLang.toLowerCase())?.name || selectedLang : 'Todos os Idiomas';
    if (section === 'level') return selectedLevel ? levels.find(l => l.id === selectedLevel)?.label || selectedLevel : 'Todos os Níveis';
    if (section === 'sort') return sortOptions.find(s => s.id === sortBy)?.label || 'Mais Relevantes';
    if (section === 'tags') return selectedTag ? tagOptions.find(t => t.id === selectedTag)?.label || selectedTag : 'Todas as Tags';
    return '';
  };

  const sections = [
    { key: 'lang', icon: <Globe2 size={16} />, title: 'Idiomas' },
    { key: 'level', icon: <Sparkles size={16} />, title: 'Dificuldade (CEFR)' },
    { key: 'sort', icon: <RefreshCw size={16} />, title: 'Exibir Por' },
    { key: 'tags', icon: <Tag size={16} />, title: 'Tags' }
  ];

  // Portal no <body>: a página tem animação com transform, que prenderia o "fixed" dentro dela
  // (o modal ficava fora da tela e só o desfoque aparecia).
  return createPortal(
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-6 bg-black/30 backdrop-blur-[20px]" onClick={onClose}>
      <div className="bg-surface w-full max-w-lg rounded-[28px] overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        <div className="p-6 md:p-8 border-b border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 text-primary rounded-2xl">
              <Zap size={24} />
            </div>
            <div>
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em]"><T k="ui.ab.advancedFilters" /></h3>
              <p className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">PERSONALIZE SUA BUSCA</p>
            </div>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {sections.map(section => (
            <div key={section.key} className="border-b border-black/[0.06] dark:border-white/[0.06] last:border-b-0">
              <button
                onClick={() => toggleSection(section.key)}
                className="w-full flex items-center justify-between px-6 md:px-8 py-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-primary">{section.icon}</span>
                  <span className="font-semibold text-sm dark:text-ink">{section.title}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold text-primary bg-primary/10 px-3 py-1 rounded-full truncate max-w-[140px]">
                    {getSelectedLabel(section.key)}
                  </span>
                  {openSection === section.key ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-ink/60" />}
                </div>
              </button>
              {openSection === section.key && (
                <div className="px-6 md:px-8 pb-5 max-h-[240px] overflow-y-auto custom-scrollbar space-y-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {section.key === 'lang' && languages.map(l => (
                    <button
                      key={l.code}
                      onClick={() => onSelectLang(l.code)}
                      className={`w-full text-left px-5 py-3 rounded-2xl text-xs font-bold transition-all ${
                        (selectedLang === l.code || (l.code && selectedLang.toLowerCase().startsWith(l.code.toLowerCase().slice(0, 2))))
                          ? 'bg-ink text-surface'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {l.name}
                    </button>
                  ))}
                  {section.key === 'level' && levels.map(lvl => (
                    <button
                      key={lvl.id}
                      onClick={() => onSelectLevel(lvl.id)}
                      className={`w-full text-left px-5 py-3 rounded-2xl text-xs font-bold transition-all ${
                        selectedLevel === lvl.id
                          ? 'bg-ink text-surface'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {lvl.label}
                    </button>
                  ))}
                  {section.key === 'sort' && sortOptions.map(sOption => (
                    <button
                      key={sOption.id}
                      onClick={() => onSelectSort(sOption.id as any)}
                      className={`w-full text-left px-5 py-3 rounded-2xl text-xs font-bold transition-all ${
                        sortBy === sOption.id
                          ? 'bg-ink text-surface'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {sOption.label}
                    </button>
                  ))}
                  {section.key === 'tags' && tagOptions.map(tOption => (
                    <button
                      key={tOption.id}
                      onClick={() => onSelectTag(tOption.id)}
                      className={`w-full text-left px-5 py-3 rounded-2xl text-xs font-bold transition-all ${
                        selectedTag === tOption.id
                          ? 'bg-ink text-surface'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {tOption.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-6 md:p-8 bg-slate-50 dark:bg-slate-800/50 border-t border-black/[0.06] dark:border-white/[0.06] shrink-0">
          <button
            onClick={onClose}
            className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-3xl hover:opacity-90 active:scale-95 transition-all cursor-pointer"
          >
            APLICAR FILTROS
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const AudiobooksPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { playAudiobook } = useSyncedAudiobook();

  // Abas: 'synced' (audiobooks com texto, R2), 'shelf' (Minha estante, rota /audiobooks/library), 'librivox'
  const isShelfRoute = location.pathname.replace(/\/+$/, '') === '/audiobooks/library';
  const [activeTab, setActiveTab] = useState<'synced' | 'librivox' | 'shelf'>(isShelfRoute ? 'shelf' : 'synced');
  useEffect(() => {
    if (isShelfRoute) setActiveTab('shelf');
    else setActiveTab(prev => (prev === 'shelf' ? 'synced' : prev));
  }, [isShelfRoute]);

  // Janela de informações + estante + avaliação
  const [selectedItem, setSelectedItem] = useState<AudiobookCardItem | null>(null);
  const [shelfItems, setShelfItems] = useState<any[]>([]);
  const [shelfLoading, setShelfLoading] = useState(false);
  const [shelfError, setShelfError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [ratingTarget, setRatingTarget] = useState<any | null>(null);

  const paramLang = searchParams.get('lang');
  const paramTargetTime = searchParams.get('targetTime');

  const [query, setQuery] = useState('');
  const [language, setLanguage] = useState(paramLang || '');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState<'favoritos' | 'mais_acessados' | 'relevancia'>('relevancia');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  useEffect(() => {
    if (paramTargetTime) {
      localStorage.setItem('libritui_audiobook_target_minutes', paramTargetTime);
    }
  }, [paramTargetTime]);
  
  // LibriVox state
  const [books, setBooks] = useState<LibriVoxBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [maxVisiblePage, setMaxVisiblePage] = useState(PAGES_PER_WINDOW);
  const [error, setError] = useState<string | null>(null);

  // Synced Audiobooks state
  const [syncedItems, setSyncedItems] = useState<any[]>([]);
  const [syncedLoading, setSyncedLoading] = useState(false);
  const [syncedError, setSyncedError] = useState<string | null>(null);
  const [syncedPage, setSyncedPage] = useState(1);
  const [syncedTotalPages, setSyncedTotalPages] = useState(1);
  const [loadingSyncedId, setLoadingSyncedId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.menu-popover-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const validateBook = (book: LibriVoxBook, lang: string): boolean => {
    if (!lang) return true;
    const textToTest = `${book.title} ${book.description}`.toLowerCase();

    switch (lang) {
      case 'English': return /\b(the|with|and)\b/i.test(textToTest);
      case 'Portuguese': return /\b(com|para|dos)\b/i.test(textToTest) || /[ãoç]/i.test(textToTest);
      case 'Italian': return /\b(il|di|che|del|nella)\b/i.test(textToTest) || /(zione|issimo|mente)\b/i.test(textToTest);
      case 'Chinese': return /[\u4e00-\u9fff]/.test(textToTest);
      case 'Spanish': return /\b(el|de|con|para)\b/i.test(textToTest) || /[ñ¿]/.test(textToTest);
      case 'French': return /\b(les|des|pour|dans)\b/i.test(textToTest) || /[çêàâû]/i.test(textToTest);
      case 'German': return /\b(und|der|die|das|mit)\b/i.test(textToTest);
      case 'Russian': return /[\u0400-\u04ff]/.test(textToTest);
      case 'Latin': return /\b(et|qui|est|in|ad)\b/i.test(textToTest);
      case 'Finnish': return /\b(ja|on|oli|hän)\b/i.test(textToTest);
      default: return true;
    }
  };

  const fetchBooks = async (logicalPage: number) => {
    setLoading(true);
    setError(null);

    let accumulated: LibriVoxBook[] = [];
    let currentApiPage = (logicalPage - 1) * 5;
    let attempts = 0;
    const MAX_ATTEMPTS = 30;
    let searchMode: 'official' | 'hybrid' = 'official';

    try {
      while (accumulated.length < ITEMS_PER_PAGE && attempts < MAX_ATTEMPTS) {
        const currentLangFilter = searchMode === 'official' ? language : '';
        const currentTitleFilter = searchMode === 'hybrid' ? language : query;

        const rawBooks = await librivoxApi.searchAudiobooks({
          query: currentTitleFilter,
          language: currentLangFilter,
          page: currentApiPage,
          limit: ITEMS_PER_PAGE
        });

        if (rawBooks.length === 0 && searchMode === 'official' && language && attempts === 0) {
          searchMode = 'hybrid';
          currentApiPage = 0;
          continue;
        }

        if (rawBooks.length === 0) break;

        const validated = rawBooks.filter((b: LibriVoxBook) => validateBook(b, language));

        validated.forEach(v => {
          if (!accumulated.find(existing => existing.id === v.id)) {
            accumulated.push(v);
          }
        });

        if (accumulated.length >= ITEMS_PER_PAGE) break;

        currentApiPage++;
        attempts++;
      }

      setBooks(accumulated.slice(0, ITEMS_PER_PAGE));
    } catch (err) {
      setError(t('audiobooks.connection_error', 'Falha na conexão. Verifique sua internet.'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncedAudiobooks = async (p: number) => {
    setSyncedLoading(true);
    setSyncedError(null);
    try {
      const langCodeMap: Record<string, string> = {
        'english': 'en', 'en': 'en',
        'portuguese': 'pt', 'português': 'pt', 'pt': 'pt',
        'spanish': 'es', 'espanhol': 'es', 'es': 'es',
        'french': 'fr', 'francês': 'fr', 'fr': 'fr',
        'german': 'de', 'alemão': 'de', 'de': 'de',
        'italian': 'it', 'italiano': 'it', 'it': 'it',
        'dutch': 'nl', 'holandês': 'nl', 'nl': 'nl'
      };
      const normalizedLang = language ? (langCodeMap[language.toLowerCase()] || language.toLowerCase().slice(0, 2)) : 'all';
      const url = `/api/sync-audiobooks/catalog?page=${p}&limit=12&lang=${normalizedLang}&search=${encodeURIComponent(query)}&sort=${sortBy}`;

      const headers: Record<string, string> = {};
      if (user?.id) headers['x-user-id'] = user.id;

      let catalogLoaded = false;
      try {
        const res = await fetch(url, { headers });
        if (res.ok) {
          const data = await res.json();
          if (data && Array.isArray(data.items)) {
            setSyncedItems(data.items);
            setSyncedTotalPages(data.totalPages || 1);
            catalogLoaded = true;
          }
        }
      } catch (e) {}

      // Fallback: Direct client fetch from /audiobooks-final-catalog.json or R2 CDN if API is unavailable
      // Reserva com os catálogos antigos: desligada na fase de teste (eles listariam os livros ocultos)
      if (!catalogLoaded && !SHOW_ONLY_TEST_BOOKS) {
        let fullList: any[] = [];
        try {
          const localRes = await fetch('/audiobooks-final-catalog.json');
          if (localRes.ok) fullList = await localRes.json();
        } catch (e) {}
        if (!fullList || fullList.length === 0) {
          try {
            const cdnRes = await fetch('https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/audiobooks-final/catalog.json');
            if (cdnRes.ok) fullList = await cdnRes.json();
          } catch (e) {}
        }
        if (fullList && fullList.length > 0) {
          let filtered = fullList;
          if (normalizedLang && normalizedLang !== 'all') {
            filtered = filtered.filter(i => (i.language || '').toLowerCase() === normalizedLang);
          }
          if (query) {
            const q = query.toLowerCase();
            filtered = filtered.filter(i => 
              (i.title && i.title.toLowerCase().includes(q)) || 
              (i.author && i.author.toLowerCase().includes(q)) ||
              (i.slug && i.slug.toLowerCase().includes(q))
            );
          }
          const total = filtered.length;
          const limit = 12;
          const totalPages = Math.ceil(total / limit) || 1;
          const start = (p - 1) * limit;
          setSyncedItems(filtered.slice(start, start + limit));
          setSyncedTotalPages(totalPages);
          catalogLoaded = true;
        }
      }

      if (!catalogLoaded) {
        throw new Error('Falha ao carregar catálogo de audiobooks sincronizados.');
      }
    } catch (err: any) {
      setSyncedError(err.message || 'Erro ao carregar audiobooks sincronizados.');
    } finally {
      setSyncedLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'librivox') {
      const timer = setTimeout(() => fetchBooks(page), 400);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => fetchSyncedAudiobooks(syncedPage), 400);
      return () => clearTimeout(timer);
    }
  }, [activeTab, query, language, page, syncedPage, sortBy]);

  const fetchShelf = async () => {
    if (!user?.id) { setShelfItems([]); return; }
    setShelfLoading(true);
    setShelfError(null);
    try {
      const res = await fetch('/api/audiobooks/shelf');
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setShelfItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setShelfError(t('audiobooks.save_error', 'Não foi possível atualizar a estante.'));
    } finally {
      setShelfLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'shelf') fetchShelf();
  }, [activeTab, user?.id]);

  const shelfAction = async (audiobookId: string, action: 'save' | 'remove' | 'favorite' | 'unfavorite' | 'opened') => {
    const res = await fetch('/api/audiobooks/shelf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audiobook_id: audiobookId, action })
    });
    if (!res.ok) throw new Error(String(res.status));
  };

  const handleSaveToShelf = async (item: AudiobookCardItem) => {
    if (!user?.id) { alert(t('audiobooks.login_required', 'Entre na sua conta para usar a estante.')); return; }
    setSavingId(item.id);
    try {
      await shelfAction(item.id, 'save');
      setSyncedItems(prev => prev.map(i => i.id === item.id ? { ...i, is_saved: true } : i));
      setSelectedItem(prev => prev && prev.id === item.id ? { ...prev, is_saved: true } : prev);
    } catch {
      alert(t('audiobooks.save_error', 'Não foi possível atualizar a estante.'));
    } finally {
      setSavingId(null);
    }
  };

  const handleRemoveFromShelf = async (item: any) => {
    setOpenMenuId(null);
    const before = shelfItems;
    setShelfItems(prev => prev.filter(i => i.id !== item.id));
    try {
      await shelfAction(item.id, 'remove');
      setSyncedItems(prev => prev.map(i => i.id === item.id ? { ...i, is_saved: false } : i));
    } catch {
      setShelfItems(before);
      alert(t('audiobooks.save_error', 'Não foi possível atualizar a estante.'));
    }
  };

  const handleShelfFavorite = async (item: any) => {
    setOpenMenuId(null);
    const next = !item.is_favorited;
    setShelfItems(prev => prev.map(i => i.id === item.id ? { ...i, is_favorited: next } : i));
    try {
      await shelfAction(item.id, next ? 'favorite' : 'unfavorite');
      setSyncedItems(prev => prev.map(i => i.id === item.id ? { ...i, is_favorited: next } : i));
    } catch {
      setShelfItems(prev => prev.map(i => i.id === item.id ? { ...i, is_favorited: !next } : i));
    }
  };

  const switchTab = (tab: 'synced' | 'librivox' | 'shelf') => {
    setActiveTab(tab);
    if (tab === 'shelf' && !isShelfRoute) navigate('/audiobooks/library', { replace: true });
    if (tab !== 'shelf' && isShelfRoute) navigate('/audiobooks', { replace: true });
  };

  const handleToggleFavorite = async (item: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setOpenMenuId(null);
    const newStatus = !item.is_favorited;

    // Optimistic UI update
    setSyncedItems(prev => prev.map(i => i.id === item.id ? { ...i, is_favorited: newStatus } : i));

    try {
      await fetch('/api/audiobooks/favorite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.id || ''
        },
        body: JSON.stringify({
          audiobook_id: item.id,
          audiobook_slug: item.slug || item.id,
          is_favorited: newStatus
        })
      });
      if (sortBy === 'favoritos') {
        fetchSyncedAudiobooks(syncedPage);
      }
    } catch (err) {
      // Rollback on error
      setSyncedItems(prev => prev.map(i => i.id === item.id ? { ...i, is_favorited: !newStatus } : i));
    }
  };

  const handleOpenSyncedBook = async (item: any) => {
    setLoadingSyncedId(item.id);
    try {
      // 1. Init protection session token
      const sessionRes = await fetch('/api/player/session/init', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.id || ''
        },
        body: JSON.stringify({ bookId: item.id })
      });
      const sessionData = sessionRes.ok ? await sessionRes.json() : {};

      // 2. Fetch audiobook metadata & initial sentences
      const infoUrl = `/api/sync-audiobooks/${encodeURIComponent(item.id)}/info?folder=${encodeURIComponent(item.folder || '')}`;
      const res = await fetch(infoUrl, { headers: sessionData.token ? { 'x-player-token': sessionData.token } : {} });
      if (!res.ok) throw new Error(`Falha ao carregar áudio sincronizado de "${item.title}".`);
      const info = await res.json();
      // v3: parágrafos vêm cifrados com a chave da sessão
      if (info.encrypted && sessionData.token) {
        info.sentences = await decryptWindowPayload(info.encrypted.ciphertext, info.encrypted.iv, sessionData.token);
      }

      // 3. Fetch state (saved time + paragraph ID) from D1
      let savedTime = 0;
      let currentParagraphId: string | undefined = undefined;
      let isFavorited = item.is_favorited;

      if (user?.id) {
        try {
          const stateRes = await fetch(`/api/audiobooks/${encodeURIComponent(item.id)}/state`, {
            headers: { 'x-user-id': user.id }
          });
          if (stateRes.ok) {
            const stateData = await stateRes.json();
            savedTime = stateData.current_time || 0;
            currentParagraphId = stateData.current_paragraph_id;
            if (typeof stateData.is_favorited === 'boolean') isFavorited = stateData.is_favorited;
          }
        } catch (e) {}
      }

      playAudiobook({
        id: `r2_${info.id}`,
        book_id: info.id,
        slug: info.slug || item.slug || item.id,
        folder: info.folder || item.folder,
        sessionToken: sessionData.token,
        title: info.title || item.title,
        author: info.author || item.author,
        language: info.language || item.language,
        total_duration_seconds: info.total_duration_seconds || item.duration_seconds,
        tracks: Array.isArray(info.tracks) && info.tracks.length > 0 ? info.tracks : undefined,
        sentences: info.sentences || [],
        audioSrc: info.audioUrl || `/api/audiobooks/${encodeURIComponent(info.id)}/stream?folder=${encodeURIComponent(info.folder || item.folder || '')}&slug=${encodeURIComponent(info.slug || item.slug || '')}`,
        addedAt: Date.now(),
        savedTime,
        currentParagraphId,
        isFavorited,
        syncOffset: loadSavedSyncOffset(info.id)
      });
      setSelectedItem(null);
      if (user?.id) shelfAction(item.id, 'opened').catch(() => {});
    } catch (err: any) {
      alert(err.message || t('audiobooks.open_error', 'Não foi possível abrir este audiobook.'));
    } finally {
      setLoadingSyncedId(null);
    }
  };

  const currentLangName = LIBRIVOX_LANGUAGES.find(l => l.code === language)?.name || 'Todos os idiomas';
  const displayLangName = language ? t(`audiobooks.lang_${language.toLowerCase()}`, currentLangName) : t('audiobooks.all_langs', 'Todos os idiomas');

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 pb-20 px-4 md:px-0">
      {/* Header */}
      <div className="space-y-4">
        <AppBackButton to="/" />
        <StudyPageHeader
          tourKey="audiobooks"
          title={t('audiobooks.pageTitle', 'Audiobooks')}
          actions={
            <span data-tour="audiobooks-links" className="flex items-center gap-2">
              <Link to="/library" className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone flex items-center justify-center transition-colors" title="Minha Estante">
                <AnimatedIcon motion="flip"><Library size={17} strokeWidth={1.75} /></AnimatedIcon>
              </Link>
              <Link to="/libritui-library" className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone flex items-center justify-center transition-colors" title="Biblioteca LibriTui">
                <AnimatedIcon motion="flip"><BookOpen size={17} strokeWidth={1.75} /></AnimatedIcon>
              </Link>
            </span>
          }
        />
      </div>

      {/* Tab Switcher: Left = Audiobooks com texto, Right = Audiobooks (LibriVox) */}
      <div className="flex justify-center my-4">
        <div data-tour="audiobooks-tabs" className="bg-surface border border-black/[0.06] dark:border-white/[0.06] p-1 rounded-full inline-flex items-center gap-1">
          <button
            onClick={() => { switchTab('synced'); setSyncedPage(1); }}
            className={`h-10 px-5 rounded-full font-medium text-xs md:text-sm flex items-center gap-2 transition-colors duration-300 cursor-pointer ${
              activeTab === 'synced'
                ? 'bg-ink text-surface'
                : 'text-muted hover:text-ink'
            }`}
          >
            <Headphones size={15} strokeWidth={1.75} />
            <span>{t('audiobooks.synced_tab', 'Audiobooks sincronizados')}</span>
          </button>
          <button
            onClick={() => switchTab('shelf')}
            className={`h-10 px-5 rounded-full font-medium text-xs md:text-sm flex items-center gap-2 transition-colors duration-300 cursor-pointer ${
              activeTab === 'shelf'
                ? 'bg-ink text-surface'
                : 'text-muted hover:text-ink'
            }`}
          >
            <Library size={15} strokeWidth={1.75} />
            <span>{t('audiobooks.shelf_tab', 'Minha estante')}</span>
          </button>
          {!SHOW_ONLY_TEST_BOOKS && <button
            onClick={() => { switchTab('librivox'); setPage(1); }}
            className={`h-10 px-5 rounded-full font-medium text-xs md:text-sm flex items-center gap-2 transition-colors duration-300 cursor-pointer ${
              activeTab === 'librivox'
                ? 'bg-ink text-surface'
                : 'text-muted hover:text-ink'
            }`}
          >
            <span>{t('audiobooks.librivox_tab', 'Audiobooks')}</span>
          </button>}
        </div>
      </div>


      {/* Search & Filter Modal Launcher (Image 2 style) */}
      {activeTab !== 'shelf' && <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="flex-1 relative group w-full">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-ink transition-colors" size={18} strokeWidth={1.75} />
          <input
            type="text"
            placeholder={t('audiobooks.search_placeholder', 'Pesquisar por título...')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (activeTab === 'librivox') { setPage(1); setMaxVisiblePage(PAGES_PER_WINDOW); }
              else { setSyncedPage(1); }
            }}
            data-tour="audiobooks-search" className="w-full h-14 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-full pl-[52px] pr-6 text-[15px] text-ink placeholder:text-muted outline-none transition-colors focus:border-ink/25"
          />
        </div>

        <div className="relative w-full md:w-72">
          <button
            onClick={() => setIsFilterModalOpen(true)}
            data-tour="audiobooks-filter" className="group w-full h-14 flex items-center justify-between bg-tint-sky text-ink rounded-full px-6 font-medium hover:opacity-90 transition-opacity"
          >
            <div className="flex items-center gap-3">
              <Globe className="group-hover:rotate-12 transition-transform duration-500" size={17} strokeWidth={1.75} />
              <span className="truncate text-sm">{displayLangName}</span>
            </div>
            <ChevronDown size={16} className="text-slate-400" />
          </button>
        </div>
      </div>}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        selectedLang={language}
        onSelectLang={(code) => { setLanguage(code); setSyncedPage(1); setPage(1); }}
        selectedLevel={selectedLevel}
        onSelectLevel={(lvl) => setSelectedLevel(lvl)}
        selectedTag={selectedTag}
        onSelectTag={(tag) => setSelectedTag(tag)}
        sortBy={sortBy}
        onSelectSort={(sort) => { setSortBy(sort); setSyncedPage(1); }}
      />

      {/* SYNCED AUDIOBOOKS TAB CONTENT ('Audiobooks com texto') */}
      {activeTab === 'synced' && (
        syncedLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="animate-spin text-warn" size={48} />
            <p className="text-slate-400 font-normal uppercase font-mono text-[10px] tracking-[0.14em]">
              {t('audiobooks.loading', 'Carregando audiobooks...')}
            </p>
          </div>
        ) : syncedError ? (
          <div className="p-10 text-center bg-tint-rose dark:bg-tint-rose/10 rounded-[28px] border border-tint-rose">
            <p className="text-bad font-bold">{syncedError}</p>
            <button onClick={() => fetchSyncedAudiobooks(syncedPage)} className="mt-4 h-10 px-5 bg-primary text-slate-900 rounded-full text-sm font-medium">{t('audiobooks.retry', 'Repetir')}</button>
          </div>
        ) : syncedItems.length === 0 ? (
          <div className="px-8 py-16 sm:p-20 text-center bg-surface rounded-[32px] border border-black/[0.06] dark:border-white/[0.06]">
            <div className="w-20 h-20 bg-tint-peach rounded-full flex items-center justify-center mx-auto mb-6 text-ink">
              <Headphones size={40} />
            </div>
            <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('audiobooks.no_synced', 'Nenhum audiobook encontrado')}</h3>
            <p className="text-muted mt-2 text-sm">{t('audiobooks.try_another', 'Tente outro termo ou mude o filtro.')}</p>
          </div>
        ) : (
          <div className="space-y-10">
            <div data-tour="audiobooks-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {syncedItems.map(item => {
                const durationFormatted = formatDuration(item.duration_seconds);
                const isOpening = loadingSyncedId === item.id;

                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className="group bg-surface p-5 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col gap-4 relative overflow-hidden"
                  >
                    {/* Language Badge */}
                    <div className="absolute top-8 left-8 z-10 bg-slate-900/80 backdrop-blur-md text-warn font-medium text-[10px] px-2.5 py-1 rounded-lg border border-warn/30">
                      {item.language ? item.language.toUpperCase() : 'EN'}
                    </div>

                    {/* 3-Dots Button Context Menu on Right Side */}
                    <div className="absolute top-8 right-8 z-20 menu-popover-container" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === item.id ? null : item.id);
                        }}
                        className="w-8 h-8 rounded-full bg-slate-900/80 backdrop-blur-md border border-white/10 text-slate-300 hover:text-white flex items-center justify-center transition-all cursor-pointer"
                        title={i18nInstance.t('ui.common.options')}
                      >
                        <MoreVertical size={16} />
                      </button>
                      {openMenuId === item.id && (
                        <div className="absolute right-0 mt-2 w-40 bg-slate-900 border border-slate-800 rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] p-1.5 z-30 animate-in fade-in zoom-in-95">
                          <button
                            onClick={(e) => handleToggleFavorite(item, e)}
                            className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 text-slate-200 hover:bg-slate-800 hover:text-warn transition-colors cursor-pointer"
                          >
                            <Heart size={14} className={item.is_favorited ? "fill-warn text-warn" : "text-slate-400"} />
                            <span>{item.is_favorited ? t('audiobooks.unfavorite', 'Remover favorito') : t('audiobooks.favorite', 'Favoritar')}</span>
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="aspect-square bg-tint-stone rounded-[22px] overflow-hidden flex items-center justify-center relative">
                      <SyncedCover item={item} />
                      <div className="absolute inset-0 bg-slate-950/40 group-hover:bg-warn/20 transition-colors flex items-center justify-center">
                        {isOpening ? (
                          <Loader2 size={36} className="animate-spin text-warn" />
                        ) : (
                          <Headphones className="text-white opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all" size={36} />
                        )}
                      </div>
                    </div>

                    <div className="space-y-1.5 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between gap-1">
                          <h3 className="font-semibold text-sm line-clamp-2 leading-tight group-hover:text-warn transition-colors">{item.title}</h3>
                          {item.is_favorited && (
                            <Heart size={14} className="fill-warn text-warn shrink-0" />
                          )}
                        </div>
                        <p className="text-[10px] font-normal text-slate-400 uppercase tracking-[0.14em] font-mono flex items-center gap-1 mt-1">
                          <User size={10} /> {item.author}
                        </p>
                      </div>

                      {/* Duração e nota estimada */}
                      {(durationFormatted || item.rating_avg) && (
                        <div className="pt-2 flex items-center gap-3 text-slate-500 dark:text-slate-400 font-bold text-xs">
                          {durationFormatted && (
                            <span className="flex items-center gap-1.5">
                              <Headphones size={13} className="text-warn shrink-0" />
                              <span>{durationFormatted}</span>
                            </span>
                          )}
                          <RatingBadge avg={item.rating_avg} count={item.rating_count} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination for Synced Tab */}
            {syncedTotalPages > 1 && (
              <div className="flex justify-center items-center gap-3 pt-10 border-t border-black/[0.06] dark:border-white/[0.06]">
                <button
                  onClick={() => setSyncedPage(p => Math.max(1, p - 1))}
                  disabled={syncedPage === 1}
                  className="w-12 h-12 flex items-center justify-center bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl disabled:opacity-20 active:scale-95 transition-all text-slate-500 hover:text-warn"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-4">
                  {syncedPage} / {syncedTotalPages}
                </span>
                <button
                  onClick={() => setSyncedPage(p => Math.min(syncedTotalPages, p + 1))}
                  disabled={syncedPage === syncedTotalPages}
                  className="w-12 h-12 flex items-center justify-center bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl disabled:opacity-20 active:scale-95 transition-all text-slate-500 hover:text-warn"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        )
      )}

      {/* MINHA ESTANTE (audiobooks salvos pelo usuário) */}
      {activeTab === 'shelf' && (
        !user?.id ? (
          <div className="px-8 py-16 sm:p-20 text-center bg-surface rounded-[32px] border border-black/[0.06] dark:border-white/[0.06]">
            <div className="w-20 h-20 bg-tint-peach rounded-full flex items-center justify-center mx-auto mb-6 text-ink">
              <Library size={36} strokeWidth={1.5} />
            </div>
            <p className="text-muted text-sm">{t('audiobooks.login_required', 'Entre na sua conta para usar a estante.')}</p>
          </div>
        ) : shelfLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="animate-spin text-warn" size={48} />
          </div>
        ) : shelfError ? (
          <div className="p-10 text-center bg-tint-rose dark:bg-tint-rose/10 rounded-[28px] border border-tint-rose">
            <p className="text-bad font-bold">{shelfError}</p>
            <button onClick={fetchShelf} className="mt-4 h-10 px-5 bg-primary text-slate-900 rounded-full text-sm font-medium">{t('audiobooks.retry', 'Repetir')}</button>
          </div>
        ) : shelfItems.length === 0 ? (
          <div className="px-8 py-16 sm:p-20 text-center bg-surface rounded-[32px] border border-black/[0.06] dark:border-white/[0.06]">
            <div className="w-20 h-20 bg-tint-peach rounded-full flex items-center justify-center mx-auto mb-6 text-ink">
              <Library size={36} strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('audiobooks.empty_shelf', 'Sua estante de audiobooks está vazia')}</h3>
            <p className="text-muted mt-2 text-sm">{t('audiobooks.empty_shelf_hint', 'Abra um audiobook e toque em Salvar na Estante.')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {shelfItems.map(item => {
              const progress = Math.max(0, Math.min(100, Math.round(item.progress_percentage || 0)));
              const isOpening = loadingSyncedId === item.id;
              return (
                <div key={item.id} className={`group relative transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1.5 ${openMenuId === `shelf_${item.id}` ? 'z-[100]' : 'z-10'}`}>
                  <div onClick={() => setSelectedItem({ ...item, is_saved: true })} className="relative aspect-square rounded-[22px] overflow-hidden bg-tint-stone cursor-pointer transition-shadow duration-500 group-hover:shadow-[0_24px_50px_-28px_rgba(0,0,0,0.55)]">
                    <SyncedCover item={item} />
                    {isOpening && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center"><Loader2 size={28} className="animate-spin text-white" /></div>
                    )}
                  </div>
                  <div onClick={() => setSelectedItem({ ...item, is_saved: true })} className="mt-3 px-1 cursor-pointer">
                    <h4 className="text-[13px] font-medium text-ink leading-snug line-clamp-2">{item.title}</h4>
                    <p className="text-[11px] text-muted mt-0.5 line-clamp-1">{item.author}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex-1 h-1 rounded-full bg-ink/[0.07] overflow-hidden"><div className="h-full bg-ink rounded-full transition-all duration-700" style={{ width: `${progress}%` }} /></div>
                      <span className="text-[10px] font-mono tabular-nums text-muted">{progress}%</span>
                    </div>
                    {(item.my_rating || item.rating_avg) && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <RatingBadge avg={item.rating_avg} count={item.rating_count} />
                        {item.my_rating ? <span className="text-[10px] text-muted font-mono">{t('ratings.your_rating', 'Sua nota')}: {item.my_rating}</span> : null}
                      </div>
                    )}
                  </div>

                  <div className="absolute top-2.5 right-2.5 z-30 flex items-center gap-1.5 menu-popover-container" onClick={e => e.stopPropagation()}>
                    {item.is_favorited && (
                      <div className="w-8 h-8 backdrop-blur-md rounded-full bg-black/35 text-[#F2A86F] flex items-center justify-center pointer-events-none">
                        <Heart size={14} strokeWidth={1.75} fill="currentColor" />
                      </div>
                    )}
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === `shelf_${item.id}` ? null : `shelf_${item.id}`)}
                        className="w-8 h-8 bg-black/35 backdrop-blur-md rounded-full text-white hover:bg-black/55 flex items-center justify-center transition-all"
                        aria-label={i18nInstance.t('ui.common.options')}
                      >
                        <MoreVertical size={14} />
                      </button>
                      {openMenuId === `shelf_${item.id}` && (
                        <div className="absolute right-0 top-full mt-2 w-48 bg-surface rounded-2xl border border-black/[0.06] dark:border-white/[0.06] p-1.5 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-[100] animate-in fade-in zoom-in-95">
                          <button onClick={() => handleShelfFavorite(item)} className="w-full flex items-center gap-3 p-3 text-sm font-medium text-ink rounded-xl hover:bg-tint-stone transition-colors">
                            <Heart size={16} strokeWidth={1.75} className={item.is_favorited ? 'fill-[#F2A86F] text-[#F2A86F]' : ''} />
                            {item.is_favorited ? t('audiobooks.unfavorite', 'Remover favorito') : t('audiobooks.favorite', 'Favoritar')}
                          </button>
                          <button onClick={() => { setOpenMenuId(null); setRatingTarget(item); }} className="w-full flex items-center gap-3 p-3 text-sm font-medium text-ink rounded-xl hover:bg-tint-stone transition-colors">
                            <Star size={16} strokeWidth={1.75} /> {t('ratings.rate', 'Avaliar')}
                          </button>
                          <button onClick={() => handleRemoveFromShelf(item)} className="w-full flex items-center gap-3 p-3 text-sm font-medium text-bad rounded-xl hover:bg-tint-rose transition-colors">
                            <Trash2 size={16} strokeWidth={1.75} /> {t('audiobooks.remove', 'Excluir')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {selectedItem && (
        <AudiobookDetailsModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onListen={(it) => handleOpenSyncedBook(it)}
          isOpening={loadingSyncedId === selectedItem.id}
          isSaved={!!selectedItem.is_saved || shelfItems.some(i => i.id === selectedItem.id)}
          isSaving={savingId === selectedItem.id}
          onSave={handleSaveToShelf}
          renderCover={(it) => <SyncedCover item={it} />}
        />
      )}

      {ratingTarget && (
        <RatingModal
          open={!!ratingTarget}
          onClose={() => setRatingTarget(null)}
          contentId={ratingTarget.id}
          contentType="audiobook"
          title={ratingTarget.title}
          author={ratingTarget.author}
          language={ratingTarget.language}
          initialRating={ratingTarget.my_rating}
          onSaved={(rating, summary) => {
            setShelfItems(prev => prev.map(i => i.id === ratingTarget.id ? { ...i, my_rating: rating, rating_avg: summary?.avg ?? i.rating_avg, rating_count: summary?.count ?? i.rating_count } : i));
            setSyncedItems(prev => prev.map(i => i.id === ratingTarget.id ? { ...i, rating_avg: summary?.avg ?? i.rating_avg, rating_count: summary?.count ?? i.rating_count } : i));
          }}
        />
      )}

      {/* LIBRIVOX AUDIOBOOKS TAB CONTENT */}
      {activeTab === 'librivox' && (
        loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="animate-spin text-primary" size={48} />
            <p className="text-slate-400 font-normal uppercase font-mono text-[10px] tracking-[0.14em]">
              {t('audiobooks.loading', 'Carregando audiobooks...')}
            </p>
          </div>
        ) : error ? (
          <div className="p-10 text-center bg-tint-rose dark:bg-tint-rose/10 rounded-[28px] border border-tint-rose">
            <p className="text-bad font-bold">{error}</p>
            <button onClick={() => fetchBooks(page)} className="mt-4 h-10 px-5 bg-primary text-slate-900 rounded-full text-sm font-medium">{t('audiobooks.retry', 'Repetir')}</button>
          </div>
        ) : books.length === 0 ? (
          <div className="px-8 py-16 sm:p-20 text-center bg-surface rounded-[32px] border border-black/[0.06] dark:border-white/[0.06]">
            <div className="w-20 h-20 bg-tint-peach rounded-full flex items-center justify-center mx-auto mb-6 text-ink">
              <BookOpen size={40} />
            </div>
            <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('audiobooks.complete_collection', 'Acervo completo carregado para este idioma')}</h3>
            <p className="text-muted mt-2 text-sm">{t('audiobooks.try_another', 'Tente outro termo ou mude o idioma.')}</p>
          </div>
        ) : (
          <div className="space-y-10">
            <div data-tour="audiobooks-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {books.map(book => {
                const durationFormatted = formatDuration(book.total_time);

                return (
                  <div
                    key={book.id}
                    onClick={() => navigate(`/audiobooks/${book.id}`)}
                    className="group bg-surface p-5 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col gap-4"
                  >
                    <div className="aspect-square bg-tint-stone rounded-[22px] overflow-hidden flex items-center justify-center relative">
                      <img
                        src={book.coverart_thumbnail}
                        className="w-full h-full object-cover"
                        alt={book.title}
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.src.includes('archive.org')) {
                            img.src = `https://covers.openlibrary.org/b/title/${encodeURIComponent(book.title)}-L.jpg`;
                          }
                          else if (img.src.includes('openlibrary.org')) {
                            img.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(book.title)}&background=4f46e5&color=fff&size=512&font-size=0.35&bold=true`;
                          }
                          else {
                            img.onerror = null;
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors flex items-center justify-center">
                        <Headphones className="text-white opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100 transition-all" size={32} />
                      </div>
                    </div>
                    <div className="space-y-1 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-semibold text-sm line-clamp-2 leading-tight group-hover:text-primary transition-colors">{book.title}</h3>
                        <p className="text-[10px] font-normal text-slate-400 uppercase tracking-[0.14em] font-mono flex items-center gap-1 mt-1">
                          <User size={10} /> {book.authors[0]?.first_name} {book.authors[0]?.last_name}
                        </p>
                      </div>

                      {/* Total Duration at Bottom */}
                      {durationFormatted && (
                        <div className="pt-2 flex items-center gap-1.5 text-slate-500 dark:text-slate-400 font-bold text-xs">
                          <Headphones size={13} className="text-primary shrink-0" />
                          <span>{durationFormatted}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center items-center gap-3 pt-10 border-t border-black/[0.06] dark:border-white/[0.06]">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-12 h-12 flex items-center justify-center bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl disabled:opacity-20 active:scale-95 transition-all text-slate-500 hover:text-primary"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex items-center gap-2">
                {(() => {
                  const currentWindowStart = Math.floor((page - 1) / PAGES_PER_WINDOW) * PAGES_PER_WINDOW + 1;
                  const pages = [];
                  for (let i = currentWindowStart; i < currentWindowStart + PAGES_PER_WINDOW; i++) {
                    pages.push(i);
                  }
                  return pages.map(p => (
                    <button
                      key={p}
                      onClick={() => {
                        if (p > maxVisiblePage) setMaxVisiblePage(p + (PAGES_PER_WINDOW - 1));
                        setPage(p);
                      }}
                      className={`w-12 h-12 rounded-2xl font-semibold text-sm transition-all active:scale-95 flex items-center justify-center ${page === p
                        ? 'bg-ink text-surface scale-110'
                        : 'bg-surface border border-black/[0.06] dark:border-white/[0.06] text-slate-500 hover:border-primary/80'
                        }`}
                    >
                      {p}
                    </button>
                  ));
                })()}
              </div>

              <button
                onClick={() => {
                  if (page + 1 > maxVisiblePage) setMaxVisiblePage(prev => prev + PAGES_PER_WINDOW);
                  setPage(p => p + 1);
                }}
                className="w-12 h-12 flex items-center justify-center bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl active:scale-95 transition-all text-slate-500 hover:text-primary"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default AudiobooksPage;
